import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_DOCS_URL } from '../../../data/links';
import {
  INTEGRATION_CATEGORIES_LEFT,
  INTEGRATION_CATEGORIES_RIGHT,
  type IntegrationBrand,
  type IntegrationCategory,
} from '../../../data/integrationCatalogue';

function BrandChip({ brand, color, soonLabel }: { brand: IntegrationBrand; color: string; soonLabel: string }) {
  const soon = brand.status === 'soon';
  return (
    <li
      title={soon ? `${brand.name} · ${soonLabel}` : brand.name}
      className={`inline-flex items-center gap-1.5 rounded-lg border px-2 py-1 text-xs font-semibold ${
        soon ? 'border-dashed border-gray-300 bg-white text-gray-500' : 'border-gray-200 bg-slate-50 text-gray-700'
      }`}
    >
      {brand.logo ? (
        <img
          src={`/images/brands/${brand.logo}.svg`}
          alt=""
          width={14}
          height={14}
          className={`h-3.5 w-3.5 ${soon ? 'opacity-60' : ''}`}
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      ) : (
        <span
          className="flex h-3.5 min-w-[14px] items-center justify-center rounded-[3px] px-0.5 text-[8px] font-bold leading-none text-white"
          style={{ backgroundColor: soon ? '#94a3b8' : color }}
          aria-hidden
        >
          {brand.initials ?? brand.name.slice(0, 2).toUpperCase()}
        </span>
      )}
      {brand.name}
    </li>
  );
}

function CategoryCard({ cat, title, soonLabel }: { cat: IntegrationCategory; title: string; soonLabel: string }) {
  return (
    <li className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-lg">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-lg text-white" style={{ backgroundColor: cat.color }}>
          <i className={cat.icon} aria-hidden />
        </span>
        <h3 className="text-base font-bold text-gray-900">{title}</h3>
      </div>
      <ul className="m-0 mt-3.5 flex list-none flex-wrap gap-1.5 p-0">
        {cat.brands.map((b) => (
          <BrandChip key={b.name} brand={b} color={cat.color} soonLabel={soonLabel} />
        ))}
      </ul>
    </li>
  );
}

/** Connectors from the hub to the three cards on one side. Drawn in the middle column on large screens only. */
function Connectors({ side }: { side: 'left' | 'right' }) {
  const flip = side === 'left' ? 'scale(-1,1) translate(-200,0)' : undefined;
  return (
    <svg viewBox="0 0 200 400" className="h-full w-full" preserveAspectRatio="none" aria-hidden>
      <g transform={flip} fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="4 6">
        <path d="M0 200 C 70 200, 90 60, 200 60" />
        <path d="M0 200 L 200 200" />
        <path d="M0 200 C 70 200, 90 340, 200 340" />
      </g>
      <g transform={flip}>
        {[60, 200, 340].map((y, i) => (
          <circle key={y} r="3.5" fill="#2563eb">
            <animateMotion dur={`${3 + i * 0.6}s`} repeatCount="indefinite" path={i === 1 ? 'M0 200 L 200 200' : `M0 200 C 70 200, 90 ${y}, 200 ${y}`} />
          </circle>
        ))}
      </g>
    </svg>
  );
}

export default function IntegrationsHub() {
  const { t } = useTranslation();
  const soonLabel = t('home.integrationsHub.soon');

  return (
    <section className="relative overflow-hidden border-t border-gray-100 bg-gradient-to-b from-white via-blue-50/40 to-white py-14 lg:py-16" aria-labelledby="integrations-hub-title">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mx-auto mb-8 max-w-3xl text-center lg:mb-10">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">{t('home.integrationsHub.eyebrow')}</p>
          <h2 id="integrations-hub-title" className="mt-3 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl lg:text-5xl">
            {t('home.integrationsHub.title')} <span className="block text-blue-600">{t('home.integrationsHub.titleHighlight')}</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg leading-relaxed text-gray-600">{t('home.integrationsHub.subtitle')}</p>
        </div>

        <div className="grid items-center gap-6 lg:grid-cols-[1fr_minmax(260px,0.9fr)_1fr] lg:gap-0">
          {/* Hub first on small screens */}
          <div className="order-first flex items-center justify-center lg:order-none lg:col-start-2 lg:row-start-1">
            <div className="relative flex h-full w-full items-center">
              <div className="absolute inset-y-0 left-0 hidden w-1/2 lg:block">
                <Connectors side="left" />
              </div>
              <div className="absolute inset-y-0 right-0 hidden w-1/2 lg:block">
                <Connectors side="right" />
              </div>
              <div className="relative mx-auto my-6 lg:my-0">
                <span className="absolute inset-0 -m-6 rounded-full bg-blue-500/10 blur-xl" aria-hidden />
                <span className="absolute inset-0 -m-10 animate-ping rounded-full border border-blue-400/30 [animation-duration:3s]" aria-hidden />
                <div className="relative flex h-44 w-44 flex-col items-center justify-center gap-2 rounded-full border-4 border-white bg-gradient-to-br from-[#1769ff] to-blue-500 text-white shadow-2xl shadow-blue-600/30">
                  <img src="https://cdn.geofleet.in/images/white-logo.png" alt="Geofleet" width={674} height={133} className="h-6 w-auto" loading="lazy" />
                  <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-blue-100">{t('home.integrationsHub.hubLabel')}</span>
                </div>
              </div>
            </div>
          </div>

          <ul className="m-0 flex list-none flex-col gap-4 p-0 lg:col-start-1 lg:row-start-1">
            {INTEGRATION_CATEGORIES_LEFT.map((cat) => (
              <CategoryCard key={cat.key} cat={cat} title={t(`home.integrationsHub.categories.${cat.key}`)} soonLabel={soonLabel} />
            ))}
          </ul>
          <ul className="m-0 flex list-none flex-col gap-4 p-0 lg:col-start-3 lg:row-start-1">
            {INTEGRATION_CATEGORIES_RIGHT.map((cat) => (
              <CategoryCard key={cat.key} cat={cat} title={t(`home.integrationsHub.categories.${cat.key}`)} soonLabel={soonLabel} />
            ))}
          </ul>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
          <Link to="/integrations" className="inline-flex items-center gap-2 rounded-full bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-gray-800">
            {t('home.integrationsHub.cta')}
            <i className="ri-arrow-right-line rtl:rotate-180" aria-hidden />
          </Link>
          <a href={API_DOCS_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-gray-900">
            {t('home.integrationsHub.apiDocs')}
            <i className="ri-external-link-line" aria-hidden />
          </a>
        </div>
      </div>
    </section>
  );
}
